import { lazy, Suspense, useMemo } from 'react';
import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Home from '../pages/home/Home';
import RouteWrapper from './RouteWrapper';
import Loader from '../../components/Loader';
import LandingPage from '../components/LandingPage';
import { UI_ROUTES } from '../../utils/routeConstants';
// import NotFound from '../pages/404/404';
import RedirectPage from 'src/components/RedirectPage';
import { mainDomain } from 'src/utils/constants';

const EmailVerificationStatus = lazy(
  () => import('src/FrontEnd/components/common/EmailVerificationStatus')
);
const LoanApplicationFrom = lazy(
  () =>
    import('src/CustomerDashboard/pages/loanApplication/LoanApplicationForm')
);

const EpcPartners = lazy(() => import('../pages/epc/components/EpcParners'));
const EPCOnboardingForm = lazy(
  () => import('../pages/epc/components/EPCOnboardingForm')
);
// const RefundPolicy = lazy(() => import('../pages/refundpolicy/RefundPolicy'));
// const CookiePolicy = lazy(() => import('../pages/cookiePolicy/cookiePolicy'));
// const ContactSales = lazy(() => import('../pages/contactSales/ContactSales'));
// const ContactUs = lazy(() => import('../pages/contactUs/ContactUs'));
const ComingSoon = lazy(() => import('../pages/comingSoon/ComingSoon'));
const WantFinance = lazy(() => import('../pages/finance/WantFinance'));
const Estimator = lazy(() => import('../pages/estimator/Calculator'));
const MySavings = lazy(() => import('../pages/estimator/MySavings'));
// const Knowledge = lazy(() => import('../pages/knowledge/Knowledge'));
const Register = lazy(
  () => import('../pages/Registration/RegistrationWrapper')
);
const Header = lazy(() => import('../components/Header/Header'));
// const AboutUs = lazy(() => import('../pages/aboutus/AboutUs'));
// const CampaignShowcase = lazy(
//   () => import('../pages/campaigns/CampaignShowcase')
// );
// const Mission = lazy(() => import('../pages/others/Mission'));
const InternalLogin = lazy(() => import('../pages/login/InternalLogin'));
const Lease = lazy(() => import('../pages/loan/Lease'));
const Dsa = lazy(() => import('../pages/dsa/Dsa'));
// const TermsAndConditions = lazy(
//   () => import('../pages/termsandconditions/Termsandconditions')
// );
const PremiumPartners = lazy(
  () => import('../pages/premiumPartners/PremiumPartners')
);
const ResidentialLoanProducts = lazy(
  () => import('../pages/loan/ResidentialLoanProducts')
);
const ResidentialLoanContact = lazy(
  () => import('../pages/loan/ResidentialLoanContact')
);
const CommercialAndIndustrialLoanProducts = lazy(
  () => import('../pages/loan/CommercialAndIndustrialLoanProducts')
);
const HousingSocietyLoanProducts = lazy(
  () => import('../pages/loan/HousingSocietyLoanProducts')
);
const PartnerRegistrationComplete = lazy(
  () =>
    import('../pages/partnerregistrationcomplete/PartnerRegistrationComplete')
);
const RegistrationComplete = lazy(
  () => import('../pages/registrationcomplete/RegistrationComplete')
);

const NewCustomerRegisterComplete = lazy(
  () => import('../pages/registrationcomplete/NewCustomerRegisterComplete')
);
const OldCustomerRegisterComplete = lazy(
  () => import('../pages/registrationcomplete/OldCustomerRegisterComplete')
);
// const SolfinPrivacyPolicy = lazy(
//   () => import('../pages/privacypolicy/SolfinPrivacypolicy')
// );
// const XsPrivacyPolicy = lazy(
//   () => import('../pages/privacypolicy/XSFinanacePrivacypolicy')
// );
const DistributorJoinV2 = lazy(
  () => import('../pages/distributor/DistributorJoin')
);
// const FairPracticeCode = lazy(
//   () => import('../pages/fairPracticeCode/FairPracticeCode')
// );
// const LeaseContact = lazy(() => import('../pages/contactSales/LeaseContact'));
// const GrievanceRedressalPolicy = lazy(
//   () => import('../pages/grievanceRedressalPolicy/GrievanceRedressalPolicy')
// );
const WaareeDealerSurvey = lazy(
  () => import('../pages/survey/WaareeDealerSurvey')
);
const Redirect = lazy(() => import('../pages/redirect/Redirect'));

const REGISTER_BASE_URL = `${mainDomain}/v2/residential/application/pre-sanction-journey`;

const ResetPasswordRedirect = () => {
  const { token } = useParams();
  return <RedirectPage to={`/v2/reset-password/${token}`} />;
};

const RegisterRedirect = () => {
  const { user } = useSelector((state) => state.auth);

  const target = useMemo(() => {
    const userTypeCode = user?.otherUserData?.user_type_code;
    const internalUserCode = user?.otherUserData?.internalUserCode || '';
    const userIdentityId = user?.otherUserData?.user_identity_id;

    if (userTypeCode === 'INTERNAL_USER' && internalUserCode) {
      return `${REGISTER_BASE_URL}?rmCode=${internalUserCode}`;
    }
    if (userTypeCode === 'EPC' && userIdentityId) {
      return `${REGISTER_BASE_URL}?epcCode=SEPC${userIdentityId}`;
    }
    return REGISTER_BASE_URL;
  }, [user]);

  return <RedirectPage to={target} />;
};

const AllRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path={UI_ROUTES.dashboard}
          element={
            <RouteWrapper>
              <Home />
              <LandingPage />
            </RouteWrapper>
          }
        />
        <Route
          exact
          path={UI_ROUTES.verifyEmailWithToken}
          element={<EmailVerificationStatus />}
        />
        <Route exact path={UI_ROUTES.signup} element={<RegisterRedirect />} />
        <Route
          exact
          path={UI_ROUTES.signupDsa}
          element={
            <RouteWrapper>
              <Register />
            </RouteWrapper>
          }
        />
        <Route
          exact
          path={UI_ROUTES.loanApplicationForm}
          element={
            <RouteWrapper>
              <LoanApplicationFrom />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.finance}
          element={
            <RouteWrapper>
              <WantFinance />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.premiumPartners}
          element={
            <RouteWrapper>
              <PremiumPartners />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.cookiePolicy}
          element={<RedirectPage to="/v2/policies/cookie-policy" />}
        />

        <Route
          path={UI_ROUTES.EPC}
          element={<RedirectPage to="/v2/epc/onboarding" />}
        />
        <Route
          path={UI_ROUTES.EpcRewards}
          element={<RedirectPage to="/v2/epc-rewards" />}
        />
        <Route
          path={UI_ROUTES.EpcPartners}
          element={
            <RouteWrapper>
              <EpcPartners />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.DSA}
          element={
            <RouteWrapper>
              <Dsa />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.aboutUs}
          element={<RedirectPage to="/v2/about-us" />}
        />
        <Route
          path={UI_ROUTES.campaignshowcase}
          element={<RedirectPage to="/v2/ad-campaigns" />}
        />
        <Route
          path={UI_ROUTES.solarCalculator}
          element={
            <RouteWrapper>
              <Estimator />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.mySavings}
          element={
            <RouteWrapper>
              <MySavings />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.residentialLoan}
          element={<RedirectPage to="/v2/jansamarth/onboarding-form" />}
        />
        <Route
          path={UI_ROUTES.residentialRooftopLoan}
          element={
            <RouteWrapper>
              <ResidentialLoanProducts />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.residentialLoanContact}
          element={
            <RouteWrapper>
              <ResidentialLoanContact />
            </RouteWrapper>
          }
        />

        <Route
          path={UI_ROUTES.commercialAndIndustrialLoan}
          element={
            <RouteWrapper>
              <CommercialAndIndustrialLoanProducts />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.housingSocietyLoan}
          element={
            <RouteWrapper>
              <HousingSocietyLoanProducts />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.knowledge}
          element={<RedirectPage to="/v2/knowledge" />}
        />

        <Route
          path={UI_ROUTES.privacyPolicy}
          element={<RedirectPage to="/v2/policies/xs-finance-privacy-policy" />}
        />
        <Route
          path={UI_ROUTES.solfinPrivacyPolicy}
          element={<RedirectPage to="/v2/policies/privacy-policy" />}
        />
        <Route
          path={UI_ROUTES.refundPolicy}
          element={
            <RedirectPage to="/v2/policies/refund-and-cancellation-policy" />
          }
        />
        <Route
          path={UI_ROUTES.solfinFairPracticeCodePolicy}
          element={<RedirectPage to="/v2/policies/fair-practice-code" />}
        />
        <Route
          path={UI_ROUTES.solfinGrievanceRedressalPolicy}
          element={
            <RedirectPage to="/v2/policies/grievance-redressal-policy" />
          }
        />
        <Route
          path={UI_ROUTES.termsAndConditions}
          element={<RedirectPage to="/v2/policies/terms-and-conditions" />}
        />
        <Route
          path={UI_ROUTES.distributorJoin}
          element={
            <>
              <Header />
              <DistributorJoinV2 />
            </>
          }
        ></Route>
        <Route
          path={UI_ROUTES.registrationComplete}
          element={<RegistrationComplete />}
        />
        <Route
          path={UI_ROUTES.custRegistrationComplete}
          element={<OldCustomerRegisterComplete />}
        />
        <Route
          path={UI_ROUTES.custRegistered}
          element={<NewCustomerRegisterComplete />}
        />
        <Route
          path={UI_ROUTES.partnerRegistrationComplete}
          element={<PartnerRegistrationComplete />}
        />
        <Route
          path={UI_ROUTES.login}
          element={<RedirectPage to="/v2/login" />}
        />
        <Route
          path={UI_ROUTES.internalLogin}
          element={
            <RouteWrapper>
              <InternalLogin />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.forgotPassword}
          element={<RedirectPage to="/v2/forgot-password" />}
        />
        <Route
          path={UI_ROUTES.resetPassword}
          element={<ResetPasswordRedirect />}
        />
        <Route
          path={UI_ROUTES.lease}
          element={
            <RouteWrapper>
              <Lease />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.comingSoon}
          element={
            <RouteWrapper>
              <ComingSoon />
            </RouteWrapper>
          }
        />
        <Route
          path={UI_ROUTES.mission}
          element={<RedirectPage to="/v2/mission" />}
        />
        <Route
          path={UI_ROUTES.contactSales}
          element={<RedirectPage to="/v2/contact" />}
        />
        <Route
          path={UI_ROUTES.contactUs}
          element={<RedirectPage to="/v2/contact" />}
        />
        <Route
          path={UI_ROUTES.leaseContact}
          element={<RedirectPage to="/v2/performance-linked-lease" />}
        />
        <Route
          path={UI_ROUTES.sitemap}
          element={<RedirectPage to="/v2/sitemap" />}
        />
        <Route
          path={UI_ROUTES.waareeDealerSurvey}
          element={<WaareeDealerSurvey />}
        />
        {/** Short URL redirect route - must be before wildcard */}
        <Route path={UI_ROUTES.redirect} element={<Redirect />} />
        {/** Adding a fallback ui to get redirection as login page and registered UI page */}
        <Route path="*" element={<Navigate to="/customer/home" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AllRoutes;
